import { DetermineProductionEnvironmentName } from '@rxap/environment';
import { ConfigService } from '@rxap/config';
import * as Sentry from '@sentry/angular';

/**
 * Determines the sentry environment based on the build info object.
 *
 * If the tier is local return undefined.
 * If the tier is production determine the environment based on the tag and branch name.
 *
 * @param environment
 */
function DetermineSentryEnvironment(environment) {
    switch (environment['tier']) {
        case 'local':
            return undefined;
        case 'production':
            return DetermineProductionEnvironmentName(environment);
        default:
            return undefined;
    }
}

/**
 * Determines the sentry release based on the build info object.
 *
 * If the tier is local return undefined.
 * If the tier is production return the tag value as release or undefined.
 *
 * @param environment
 */
function DetermineSentryRelease(environment) {
    switch (environment['tier']) {
        case 'local':
            return undefined;
        case 'production':
            return environment.tag ?? undefined;
        default:
            return undefined;
    }
}

function SentryInit(environment) {
    const dsn = ConfigService.Get('sentry.dsn', environment.sentry?.dsn, ConfigService.Config);
    if (!dsn) {
        console.warn('No sentry dsn provided. Sentry will not be initialized.');
    }
    Sentry.init({
        dsn: environment.sentry?.dsn,
        enabled: ConfigService.Get('sentry.enabled', environment.sentry?.enabled ?? false, ConfigService.Config),
        debug: ConfigService.Get('sentry.debug', environment.sentry?.debug ?? false, ConfigService.Config),
        environment: DetermineSentryEnvironment(environment),
        release: DetermineSentryRelease(environment),
        tunnel: ConfigService.Get('sentry.tunnel', environment.production ? '/tunnel' : undefined, ConfigService.Config),
        initialScope: {
            user: { ip_address: '{{auto}}' },
            tags: {
                origin: location.origin,
                host: location.host,
            },
        },
        replaysSessionSampleRate: ConfigService.Get('sentry.replaysSessionSampleRate', 1.0, ConfigService.Config),
        replaysOnErrorSampleRate: ConfigService.Get('sentry.replaysOnErrorSampleRate', 1.0, ConfigService.Config),
        autoSessionTracking: ConfigService.Get('sentry.autoSessionTracking', true, ConfigService.Config),
        maxValueLength: ConfigService.Get('sentry.maxValueLength', Number.MAX_SAFE_INTEGER, ConfigService.Config),
        integrations: [
            Sentry.httpClientIntegration({
                failedRequestTargets: environment.sentry?.integrations?.httpClient?.failedRequestTargets,
            }),
            Sentry.browserTracingIntegration({}),
            Sentry.replayIntegration({
                // Additional SDK configuration goes in here, for example:
                maskAllText: true,
                blockAllMedia: true,
                maskAllInputs: true,
            }),
        ],
        tracesSampleRate: ConfigService.Get('sentry.tracesSampleRate', 1.0, ConfigService.Config),
        ignoreErrors: [
            'Non-Error exception captured',
        ],
    });
}

// region 
// endregion

/**
 * Generated bundle index. Do not edit.
 */

export { DetermineSentryEnvironment, DetermineSentryRelease, SentryInit };
